import { Alert } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import Loading from 'common/components/Loading';
import useSearchParams from 'common/hooks/useSearchParams';

import { BadgeTypes, UNIT_INCH, UNIT_MM } from '@domains/Badges/constants';
import { parseBadgeData } from '@domains/Badges/designer-service';

import Builder from '../../Builder';
import { normalizeUnit } from '../../Builder/constants';
import { usePersonQuery } from '../../queries';
import { useSelectedLabelsQuery } from '../queries';

import { MOCK_PERSON_DATA_FOR_TESTING } from './constants';

const PageStyle = createGlobalStyle`
  /* Hide elements that cause blank pages to appear */
  .ReactQueryDevtools, .jodit-box {
    display: none;
  }
`;

const BadgeStyle = styled.div`
  // Set the size and orientation of the page that is rendered into the PDF (or cmd-p print dialog)
  // This relies on passing playwright the 'prefer_css_page_size' param
  // https://playwright.dev/python/docs/api/class-page#page-pdf-option-prefer-css-page-size
  @page badge_${props => props.id} {
    size: ${({ width, height }) => `${width * 4}px ${height * 4}px`};
    // All of the badges need to be rendered with a height of 57mm/2.24" (61mm/2.4" when
    // adjusted for the forced printer padding) based on how the printer is configured.
    ${({ $orientation }) => ($orientation === 'vertical' ? 'page-orientation: rotate-left;' : '')}
    page-break-after: always;
    break-after: page;
    margin: 0;
  }

  // prettier-ignore
  page: badge_${props => props.id};
`;

const EMPTY_BACKGROUND = {
  color: null,
  image: { id: null, url: null },
  size: null,
};

const BadgeDesign = ({ label, person }) => {
  const { badge, design } = parseBadgeData(label);

  return (
    <BadgeStyle
      id={badge.id}
      width={normalizeUnit(badge.size.innerBadgeSize.width, badge.size.unit)}
      height={normalizeUnit(badge.size.innerBadgeSize.height, badge.size.unit)}
      $orientation={badge.orientation}
    >
      <Builder
        badge={{
          badgeType: BadgeTypes.SIMPLE_LABEL,
          size: badge.size,
          orientation: badge.orientation,
          background: EMPTY_BACKGROUND,
          widgets: design.widgets,
          isBleedEnabled: false,
        }}
        isLoading={false}
        person={person}
        showBorder={false}
      />
    </BadgeStyle>
  );
};

BadgeDesign.propTypes = {
  label: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    size: PropTypes.shape({
      width: PropTypes.string.isRequired,
      height: PropTypes.string.isRequired,
      unitType: PropTypes.oneOf([UNIT_MM, UNIT_INCH]).isRequired,
    }).isRequired,
    designData: PropTypes.shape({
      backgroundColor: PropTypes.string,
      backgroundImage: PropTypes.string,
      backgroundSize: PropTypes.string,
      widgets: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string.isRequired,
          x: PropTypes.number.isRequired,
          y: PropTypes.number.isRequired,
          z: PropTypes.number.isRequired,
          width: PropTypes.string.isRequired,
          height: PropTypes.string.isRequired,
        }),
      ),
    }),
  }).isRequired,
  person: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    companyName: PropTypes.string,
    title: PropTypes.string,
    checkinCode: PropTypes.string,
    customFields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        value: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
};

export const CaptureLabelPDF = () => {
  const query = useSearchParams();
  const token = query.get('token');
  const personId = query.get('people_id');
  const useMockPeopleData = query.get('use_mock_people_data')?.toLowerCase() === 'true';
  const { eventId } = useParams();

  const {
    data: labelsData,
    isLoading: isLabelsLoading,
    error: labelsError,
  } = useSelectedLabelsQuery(eventId, token);

  const { data: personData, error: personError } = usePersonQuery(eventId, personId, token, {
    enabled: !useMockPeopleData,
  });

  const errorMessage = labelsError?.message || personError?.message;
  if (errorMessage) {
    return <Alert type="error" message={errorMessage} showIcon />;
  }

  const person = useMockPeopleData ? MOCK_PERSON_DATA_FOR_TESTING : personData;
  const isLoading = isLabelsLoading || !person;

  return (
    <>
      <PageStyle />
      {isLoading && <Loading />}
      {!isLoading &&
        labelsData.data.map((label, i) => (
          <BadgeDesign
            // We can have the same label multiple times, so .id isn't an option.
            // The list will never change though, so using the index is fine.
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            label={label}
            person={person}
          />
        ))}
    </>
  );
};
